import { useEffect, useLayoutEffect, useState, useRef, ReactNode, ReactElement, cloneElement, useId } from 'react';
import { createPortal } from 'react-dom';

export type TooltipSide = 'top' | 'right' | 'bottom' | 'left';

export interface TooltipProps {
  content: ReactNode;
  side?: TooltipSide;
  delay?: number;
  children: ReactElement;
}

/**
 * Tooltip renderowany w portalu, żeby nie był ucinany przez overflow paneli.
 */
export function Tooltip({ content, side = 'top', delay = 300, children }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  const show = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setVisible(true), delay);
  };
  const hide = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
    setPosition(null);
  };

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  useLayoutEffect(() => {
    if (!visible) return;
    const updatePosition = () => {
      const trigger = triggerRef.current?.getBoundingClientRect();
      const tooltip = tooltipRef.current?.getBoundingClientRect();
      if (!trigger || !tooltip) return;
      const gap = 6;
      const margin = 8;
      let resolvedSide = side;
      if (side === 'top' && trigger.top - tooltip.height - gap < margin) resolvedSide = 'bottom';
      if (side === 'bottom' && trigger.bottom + tooltip.height + gap > window.innerHeight - margin) resolvedSide = 'top';
      if (side === 'left' && trigger.left - tooltip.width - gap < margin) resolvedSide = 'right';
      if (side === 'right' && trigger.right + tooltip.width + gap > window.innerWidth - margin) resolvedSide = 'left';

      let left = trigger.left + (trigger.width - tooltip.width) / 2;
      let top = trigger.top + (trigger.height - tooltip.height) / 2;
      if (resolvedSide === 'top') top = trigger.top - tooltip.height - gap;
      if (resolvedSide === 'bottom') top = trigger.bottom + gap;
      if (resolvedSide === 'left') left = trigger.left - tooltip.width - gap;
      if (resolvedSide === 'right') left = trigger.right + gap;
      left = Math.max(margin, Math.min(left, window.innerWidth - tooltip.width - margin));
      top = Math.max(margin, Math.min(top, window.innerHeight - tooltip.height - margin));
      setPosition({ left, top });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [content, side, visible]);

  const childrenProps = (children.props as Record<string, unknown>) ?? {};

  const trigger = cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      show();
      (childrenProps.onMouseEnter as ((e: React.MouseEvent) => void) | undefined)?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hide();
      (childrenProps.onMouseLeave as ((e: React.MouseEvent) => void) | undefined)?.(e);
    },
    onFocus: (e: React.FocusEvent) => {
      show();
      (childrenProps.onFocus as ((e: React.FocusEvent) => void) | undefined)?.(e);
    },
    onBlur: (e: React.FocusEvent) => {
      hide();
      (childrenProps.onBlur as ((e: React.FocusEvent) => void) | undefined)?.(e);
    },
    'aria-describedby': visible ? id : undefined,
  } as React.HTMLAttributes<HTMLElement>);

  return (
    <span ref={triggerRef} className="relative inline-flex">
      {trigger}
      {visible && typeof document !== 'undefined' && createPortal(
        <span
          ref={tooltipRef}
          id={id}
          role="tooltip"
          style={{ left: position?.left ?? 0, top: position?.top ?? 0, visibility: position ? 'visible' : 'hidden' }}
          className={[
            'fixed z-[10000] pointer-events-none w-max max-w-[min(320px,calc(100vw-16px))]',
            'px-2 py-1 rounded-lg bg-surface-overlay border border-border text-xxs leading-relaxed text-text-primary shadow-lg',
            'animate-fade-in',
          ].join(' ')}
        >
          {content}
        </span>,
        document.body,
      )}
    </span>
  );
}
